/**
 * Country redirect popup
 *
 * Shows a modal when the visitor's detected country (Shopify's
 * /browsing_context_suggestions.json) matches one of the ISO codes on the
 * `countries` attribute. Closing hides it for the session; clicking a CTA
 * with data-remember="true" hides it for `cookie-days` days.
 */
if (!customElements.get('country-redirect-popup')) {
  class CountryRedirectPopup extends HTMLElement {
    static SESSION_KEY = 'vivo:country-popup-closed';

    connectedCallback() {
      this.cookieName = this.getAttribute('cookie-name') || 'vivo_country_popup_dismissed';
      this.cookieDays = parseInt(this.getAttribute('cookie-days'), 10) || 60;
      this.delay = parseInt(this.getAttribute('apparition-delay'), 10) || 0;
      this.onlyOnce = this.hasAttribute('only-once');
      this.countries = (this.getAttribute('countries') || '')
        .split(',')
        .map((code) => code.trim().toLowerCase())
        .filter(Boolean);

      this.content = this.querySelector('.country-redirect-popup__content');
      this.textNodes = this.querySelectorAll('[data-country-text]');

      this.querySelectorAll('[data-close]').forEach((el) => {
        el.addEventListener('click', () => this.hide());
      });

      this.querySelectorAll('[data-action]').forEach((el) => {
        el.addEventListener('click', () => {
          if (el.dataset.remember === 'true') this.setCookie();
          this.hide();
        });
      });

      this._onKeyup = (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) this.hide();
      };
      document.addEventListener('keyup', this._onKeyup);

      if (window.Shopify && window.Shopify.designMode) {
        this.bindEditorEvents();
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this._onKeyup);
      this.unlockScroll();
    }

    // In the theme editor, preview the popup whenever its section is selected
    bindEditorEvents() {
      const section = this.closest('.shopify-section');
      if (!section) return;

      section.addEventListener('shopify:section:select', () => this.show());
      section.addEventListener('shopify:section:deselect', () => this.hide());
      section.addEventListener('shopify:block:select', () => this.show());
    }

    async init() {
      if (!this.countries.length) return;
      if (this.onlyOnce && (this.hasCookie() || this.closedThisSession())) return;

      try {
        const response = await fetch(`${window.location.origin}/browsing_context_suggestions.json`);
        if (!response.ok) return;

        const data = await response.json();
        const country = data && data.detected_values && data.detected_values.country;
        const handle = country && country.handle ? country.handle.toLowerCase() : '';

        if (!handle || !this.countries.includes(handle)) return;

        this.renderCountry(country);
        setTimeout(() => this.show(), this.delay * 1000);
      } catch (error) {
        console.error('[country-redirect-popup] Failed to fetch browsing context suggestions:', error);
      }
    }

    renderCountry(country) {
      const name = country.name || '';

      this.textNodes.forEach((node) => {
        node.innerHTML = node.innerHTML.replace(/%%COUNTRY%%/g, name);
      });
    }

    show() {
      if (this.hasAttribute('open')) return;
      this.setAttribute('open', '');
      this.lockScroll();
      if (this.content) this.content.focus({ preventScroll: true });
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.unlockScroll();
      this.markClosedThisSession();
    }

    lockScroll() {
      this._previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this._previousOverflow === undefined) return;
      document.documentElement.style.overflow = this._previousOverflow;
      this._previousOverflow = undefined;
    }

    // --- persistence -------------------------------------------------------

    hasCookie() {
      return document.cookie.split(';').some((item) => item.trim().startsWith(`${this.cookieName}=`));
    }

    setCookie() {
      const expires = new Date(Date.now() + this.cookieDays * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${this.cookieName}=1; expires=${expires}; path=/; SameSite=Lax`;
    }

    closedThisSession() {
      try {
        return sessionStorage.getItem(CountryRedirectPopup.SESSION_KEY) === '1';
      } catch (error) {
        return false;
      }
    }

    markClosedThisSession() {
      if (window.Shopify && window.Shopify.designMode) return;
      try {
        sessionStorage.setItem(CountryRedirectPopup.SESSION_KEY, '1');
      } catch (error) {
        // sessionStorage unavailable (private mode) — popup will simply show again next page
      }
    }
  }

  customElements.define('country-redirect-popup', CountryRedirectPopup);
}
